// @ts-check

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';

// core
import {
  appRoles,
  AuthContext,
  cloneCampaignAndNavigateToDetails,
  CloneCampaignFlowModal,
  DataTable,
  DBPagination,
  PageTitle,
  resolveRouteWithId,
  routerUrl,
  useFeatureFlag,
} from 'core';
import { buildCampaignTableConfig } from 'core/components/Tables/configs';

// state
import { useAppDispatch, useAppSelector } from 'core/store';
import {
  cloneCampaignAsync,
  getCampaignListAsync,
  resetAlertMessages,
  resetCampaign,
  resetCampaignPreview,
  setSelectedRoute,
} from 'core/store/slices';

// Local components.
import { SearchForm } from './components/SearchForm';

const CampaignSearch = () => {
  const history = useHistory();
  const { userRoles } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const {
    campaigns,
    isLastPage,
    loading: campaignListLoading,
  } = useAppSelector((state) => state.campaignList);

  const isCloningEnabled = useFeatureFlag(
    'ACAN_CPBD_CLONING_INTEGRATION_387594',
  );

  const campaignSearchTableConfig = useMemo(
    () =>
      buildCampaignTableConfig({
        enableCloneColumn: isCloningEnabled,
      }),
    [isCloningEnabled],
  );

  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [campaignToClone, setCampaignToClone] = useState(null);

  const searchCampaign = useCallback(
    ({ page = 1 } = {}) => {
      dispatch(
        getCampaignListAsync({
          searchFilter: {
            all: userRoles.includes(appRoles.ADMIN),
            page,
            perPage: 50,
          },
        }),
      );
    },
    [dispatch, userRoles],
  );

  useEffect(() => {
    dispatch(setSelectedRoute(routerUrl.CAMPAIGN_SEARCH));
    searchCampaign();
  }, [dispatch, searchCampaign]);

  const pageChange = useCallback(
    (page) => {
      searchCampaign({
        page,
      });
    },
    [searchCampaign],
  );

  const closeCloneModal = () => {
    setCloneModalOpen(false);
    setCampaignToClone(null);
  };

  /**
   * Wrapper to keep callsites explicit and small.
   * @param {string} sourceCampaignId
   * @param {string} cloneName
   */
  const cloneToDetails = (sourceCampaignId, cloneName) =>
    cloneCampaignAndNavigateToDetails({
      dispatch,
      history,
      sourceCampaignId,
      cloneName,
      cloneCampaignAsync,
      resetAlertMessages,
      resetCampaignPreview,
      onClose: closeCloneModal,
      onAfterCloneSuccess: () => {
        dispatch(resetCampaign());
      },
    });

  /**
   * Clone the selected campaign using the provided name, then navigate to the
   * newly-created campaign Details page.
   *
   * Note:
   * Clears any existing banner alerts before starting.
   * Errors are handled by the clone thunk (banner error state); simply
   * short-circuit on failure.
   * Intentionally skip a list refresh here; the user is taken directly to
   * the cloned campaign.
   * @param {string} cloneName
   */
  const handleCloneAndRefresh = async (cloneName) => {
    const sourceCampaignId = campaignToClone?.campaign_id;
    if (!sourceCampaignId) return;

    await cloneToDetails(sourceCampaignId, cloneName);
  };

  const cloneSourceIncludesFile = Boolean(campaignToClone?.filename);

  return (
    <section>
      <header>
        <PageTitle title="Search campaigns" />
      </header>
      <section>
        <SearchForm />
      </section>
      <DataTable
        testId="tbl-search-campaigns"
        loading={campaignListLoading}
        config={campaignSearchTableConfig}
        rows={campaigns}
        height="520px"
        onCellClick={({ col, id, row }) => {
          if (isCloningEnabled && col?.id === 'clone_campaign') {
            setCampaignToClone(row);
            setCloneModalOpen(true);
            return;
          }

          if (col.id === 'view_details') {
            dispatch(resetCampaign());
            dispatch(resetCampaignPreview());
            const url = resolveRouteWithId(routerUrl.CAMPAIGN_DETAILS, id);
            history.push(url);
          }
        }}
      />
      <CloneCampaignFlowModal
        isOpen={isCloningEnabled && cloneModalOpen}
        campaignName={campaignToClone?.name}
        sourceIncludesFile={cloneSourceIncludesFile}
        onCloneAndRefresh={handleCloneAndRefresh}
        onClose={closeCloneModal}
      />
      <div className="mt-2 d-flex justify-content-end">
        <DBPagination
          loading={campaignListLoading}
          isLastPage={isLastPage}
          onChange={pageChange}
        />
      </div>
    </section>
  );
};

export { CampaignSearch };
